import * as Sentry from '@sentry/angular';

import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ZodError } from 'zod';
import { SignedOutError } from 'src/app/common/errors/signed-out-error';

@Injectable({
  providedIn: 'root',
})
export class SentryService {
  public setUserId(userId: string): void {
    Sentry.setUser({ id: userId });
  }

  public setOperator(operator: string): void {
    Sentry.setTag('operator', operator);
  }

  public captureException<T extends Record<string, unknown>>(
    error: unknown,
    context?: string,
    data?: T,
  ): void {
    if (error instanceof SignedOutError) {
      Sentry.captureException(normalizeError(error), {
        level: 'warning',
        extra: {
          ...(context && { context }),
          ...data,
        },
      });
      console.warn(`SignedOutError: ${error.message}`, error);
      return;
    }

    if (error instanceof HttpErrorResponse) {
      const urlPath = normalizePath(error.url ?? undefined);
      const errorBody = extractBodySnippet(error.error);
      const innerError = firstHttpInnerError(error);
      const code = innerError?.code ?? undefined;
      const status400 = error.status >= 400 && error.status < 500;
      const severityLevel: Sentry.SeverityLevel =
        code !== undefined && status400 ? 'warning' : 'error';

      Sentry.captureException(normalizeError(error), {
        level: severityLevel,
        tags: { ...(urlPath && { urlPath }), ...(code && { code }) },
        extra: {
          status: `${error.status}`,
          ...(context && { context }),
          ...(errorBody && { errorBody }),
          ...(code && { innerErrorCode: code }),
          ...data,
        },
        fingerprint: ['{{ default }}', urlPath ?? 'unknown-path', String(error.status)],
      });
      console[severityLevel === 'warning' ? 'warn' : 'error'](
        `HttpErrorResponse: ${error.message}`,
        error,
      );
      return;
    }

    if (error instanceof ZodError) {
      Sentry.captureException(normalizeError(error), {
        extra: {
          zodIssues: error.issues,
          ...(context && { context }),
          ...data,
        },
      });
      console.error(`ZodError: ${error.message}`, error);
      return;
    }

    const normalizedError = normalizeError(error);
    Sentry.captureException(normalizedError, {
      extra: {
        ...(context && { context }),
        ...data,
      },
    });
    console.error(`Error: ${normalizedError.message}`, normalizedError);
  }

  public captureMessage(message: string): void {
    Sentry.captureMessage(message);
  }

  public addBreadcrumb(name: string, params: object = {}): void {
    Sentry.addBreadcrumb({
      category: 'log',
      message: name,
      data: params,
      level: 'info',
    });
  }
}

function extractBodySnippet(body: unknown, maxLength = 2048): string | undefined {
  if (body == null) {
    return undefined;
  }
  let text: string | undefined;
  if (typeof body === 'string') {
    text = body;
  } else if (body && typeof body === 'object') {
    try {
      text = JSON.stringify(body);
    } catch {
      text = '[unserializable]';
    }
  } else {
    text = `${body}`;
  }
  return text?.slice(0, maxLength);
}

function firstHttpInnerError(
  error: HttpErrorResponse,
): { code?: string; [key: string]: unknown } | undefined {
  const innerErrors = error.error?.errors;
  if (
    Array.isArray(innerErrors) &&
    innerErrors.length > 0 &&
    typeof innerErrors[0] === 'object' &&
    innerErrors[0] !== null
  ) {
    return innerErrors[0];
  }
  return undefined;
}

function normalizeError(e: unknown): Error {
  if (e instanceof SignedOutError) {
    const err = new Error(e.message, { cause: e });
    err.name = 'SignedOutError';
    return err;
  }

  if (e instanceof HttpErrorResponse) {
    const err = new Error(e.message, { cause: e });
    err.name = 'HttpErrorResponse';
    return err;
  }

  if (e instanceof ZodError) {
    const err = new Error(e.message, { cause: e });
    err.name = 'ZodError';
    return err;
  }

  if (e instanceof DOMException) {
    const err = new Error(e.message, { cause: e });
    err.name = 'DOMException';
    return err;
  }

  if (e instanceof Error) {
    return e;
  }

  if (typeof e === 'object' && e !== null) {
    try {
      return new Error(JSON.stringify(e, null, 2));
    } catch {
      return new Error('[Unknown object with circular structure]');
    }
  }

  if (typeof e === 'string') {
    return new Error(e);
  }

  if (typeof e === 'number' || typeof e === 'boolean') {
    return new Error(`Non-error thrown: ${String(e)}`);
  }

  // Fallback
  return new Error('Unknown error');
}

function normalizePath(rawUrl?: string | null): string | undefined {
  if (!rawUrl) return undefined;
  try {
    const { pathname } = new URL(rawUrl, window.location.origin);
    return pathname.replace(/\/\d+(\b|$)/g, '/:id'); // strip numeric ids
  } catch {
    return rawUrl;
  }
}
